import React, { useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { components } from 'cng-web-lib'

const {
  button: { CngButton }
} = components

const MAX_PREVIEW_BILLS = 3

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    width: 702,
    maxWidth: '100%'
  },
  titleRoot: {
    padding: theme.spacing(2.5, 3, 1.5)
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.error.main
  },
  closeBtn: {
    padding: 4,
    color: theme.palette.text.secondary
  },
  infoBox: {
    background: theme.palette.background.sectionOddBg || theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: theme.spacing(0.6, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`
    }
  },
  infoLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: 'right',
    wordBreak: 'break-all'
  },
  warningBanner: {
    display: 'flex',
    gap: theme.spacing(1.5),
    background: theme.palette.error.light + '22',
    border: `1px solid ${theme.palette.error.light}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1)
  },
  warningIcon: {
    color: theme.palette.error.main,
    marginTop: 2,
    flexShrink: 0
  },
  warningText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    lineHeight: 1.6
  },
  viewMoreLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' }
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3)
  },
  // Confirm-step dialog
  confirmDialogPaper: {
    borderRadius: 12,
    width: 546,
    maxWidth: '100%'
  }
}))

/**
 * DeleteDraftsDialog — two-step delete confirmation for draft bills.
 *
 * Props:
 *  open       - boolean
 *  row        - upload history row object
 *  onClose    - fn()
 *  onConfirm  - fn()
 *  submitting - boolean
 */
function DeleteDraftsDialog({ open, row, onClose, onConfirm, submitting }) {
  const classes = useStyles()
  const [showAllBills, setShowAllBills] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (!row) return null

  const allBills = Array.isArray(row.manifestHeaderIds) ? row.manifestHeaderIds : []
  const billCount = allBills.length
  const previewBills = showAllBills ? allBills : allBills.slice(0, MAX_PREVIEW_BILLS)
  const hasMore = !showAllBills && billCount > MAX_PREVIEW_BILLS
  const billsDisplay = previewBills.join(', ') + (hasMore ? ' ...' : '')

  function handleClose() {
    setShowAllBills(false)
    setConfirmOpen(false)
    onClose()
  }

  function handleDeleteClick() {
    setConfirmOpen(true)
  }

  function handleConfirmDelete() {
    setConfirmOpen(false)
    onConfirm()
  }

  return (
    <>
      {/* Main delete dialog */}
      <Dialog
        open={open && !confirmOpen}
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
        disableBackdropClick={submitting}
        disableEscapeKeyDown={submitting}
      >
        {/* Header */}
        <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
          <Box className={classes.titleRow}>
            <Typography className={classes.titleText}>
              <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} />
              Delete Draft(s)
            </Typography>
            <IconButton size='small' className={classes.closeBtn} onClick={handleClose} disabled={submitting}>
              <FontAwesomeIcon icon={['fal', 'xmark']} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent style={{ padding: '20px 24px 12px' }}>
          {/* Detail rows */}
          <Box className={classes.infoBox}>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>File Upload ID:</Typography>
              <Typography className={classes.infoValue}>{row.id || '—'}</Typography>
            </Box>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>File Name:</Typography>
              <Typography className={classes.infoValue}>{row.fileName || '—'}</Typography>
            </Box>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Draft Bill(s):</Typography>
              <Typography className={classes.infoValue} style={{ maxWidth: 320 }}>
                {billCount === 0 ? '—' : (
                  <>
                    {billsDisplay}
                    {hasMore && (
                      <>
                        {' '}
                        <span className={classes.viewMoreLink} onClick={() => setShowAllBills(true)}>
                          View More
                        </span>
                      </>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          </Box>

          {/* Warning banner */}
          <Box className={classes.warningBanner}>
            <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} className={classes.warningIcon} />
            <Typography className={classes.warningText}>
              <strong>Warning:</strong> This will permanently delete the <strong>{billCount}</strong> draft record(s) created from this upload.
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>

        {/* Footer */}
        <Box className={classes.footer}>
          <CngButton color='secondary' size='medium' disabled={submitting} onClick={handleClose}>
            Cancel
          </CngButton>
          <CngButton
            color='primary'
            size='medium'
            disabled={submitting}
            onClick={handleDeleteClick}
            startIcon={<FontAwesomeIcon icon={['fal', 'trash-can']} />}
          >
            Yes, Delete Draft(s)
          </CngButton>
        </Box>
      </Dialog>

      {/* Second-step confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        classes={{ paper: classes.confirmDialogPaper }}
        disableBackdropClick={submitting}
        disableEscapeKeyDown={submitting}
      >
        <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
          <Box className={classes.titleRow}>
            <Typography className={classes.titleText}>
              <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} />
              Confirm Delete
            </Typography>
            <IconButton
              size='small'
              className={classes.closeBtn}
              onClick={() => !submitting && setConfirmOpen(false)}
              disabled={submitting}
            >
              <FontAwesomeIcon icon={['fal', 'xmark']} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent style={{ padding: '20px 24px 12px' }}>
          <Typography variant='body2' style={{ lineHeight: 1.7 }}>
            This will permanently delete the <strong>{billCount}</strong> draft record(s) created from this upload.
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <Box className={classes.footer}>
          <CngButton
            color='secondary'
            size='medium'
            disabled={submitting}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </CngButton>
          <CngButton
            color='danger'
            size='medium'
            disabled={submitting}
            onClick={handleConfirmDelete}
            startIcon={
              submitting
                ? <FontAwesomeIcon icon={['fal', 'spinner']} spin />
                : <FontAwesomeIcon icon={['fal', 'trash-can']} />
            }
          >
            {submitting ? 'Deleting...' : 'Yes, Delete Draft(s)'}
          </CngButton>
        </Box>
      </Dialog>
    </>
  )
}

export default DeleteDraftsDialog